import { OutletListDTO } from "@zenith/services/user";
import { GLOBAL_CONTEXT_ACTION_TYPE } from "./actions";
import { initGlobalContext } from "./globalContext";
import { ActionType, GlobalContextType, OutletContextType } from "./types";

/**
 * Keeps the outlet view pointing at outlets that still exist.
 *
 * Falls back to the merchant's default outlet when the view is empty or names
 * outlets that have since gone, so a stale view can never leave a screen with
 * nothing to scope to.
 */
const reconcileOutletSelection = (
  selection: OutletContextType["selection"],
  outlets: OutletListDTO[]
): OutletContextType["selection"] => {
  // An empty list means the outlets are still loading, not that the view is
  // stale. Dropping the selection here is what let a navigation reset All
  // Outlets to whichever outlet the server happened to be signed into.
  if (outlets.length === 0) return selection ?? null;

  if (selection?.kind === "all") return { kind: "all" };

  if (selection?.kind === "outlets") {
    const ids = selection.ids.filter((id) =>
      outlets.some((outlet) => outlet.id === id)
    );

    if (ids.length > 0) return { kind: "outlets", ids };
  }

  const fallback =
    outlets.find((outlet) => outlet.isCurrent) ??
    outlets.find((outlet) => outlet.isDefault) ??
    outlets[0];

  return fallback?.id ? { kind: "outlets", ids: [fallback.id] } : null;
};

/**
 * The global context reducer.
 * @param globalContext The global context.
 * @param action The global context action.
 * @returns A GlobalContextType.
 */
export const globalContextReducer = (
  globalContext: GlobalContextType,
  action: ActionType
): GlobalContextType => {
  switch (action.type) {
    //#region Global Context

    case GLOBAL_CONTEXT_ACTION_TYPE.CLEAR_ALL_CONTEXTS: {
      return initGlobalContext;
    }

    case GLOBAL_CONTEXT_ACTION_TYPE.CLEAR_UNUSED_CONTEXTS: {
      return {
        ...initGlobalContext,
        userDetailsContext: globalContext.userDetailsContext,
        registrationContext: globalContext.registrationContext,
        // The merchant's outlet view is a standing choice, not an in-flight
        // form, so navigating between menus must not clear it. Dropping it
        // here is what made All Outlets fall back to whichever outlet the
        // server happened to be signed into.
        outletContext: globalContext.outletContext,
      };
    }

    //#endregion Global Context

    //#region Loading Context

    case GLOBAL_CONTEXT_ACTION_TYPE.SET_LOADING: {
      const loading = action.payload;

      return {
        ...globalContext,
        loadingContext: {
          loading,
        },
      };
    }

    //#endregion Loading Context

    //#region Mobile Verification Context

    case GLOBAL_CONTEXT_ACTION_TYPE.SET_COUNTRY_CODE: {
      const countryCode = action.payload;

      return {
        ...globalContext,
        mobileVerificationContext: {
          ...globalContext.mobileVerificationContext,
          countryCode,
        },
      };
    }

    case GLOBAL_CONTEXT_ACTION_TYPE.SET_EMAIL: {
      const email = action.payload;

      return {
        ...globalContext,
        mobileVerificationContext: {
          ...globalContext.mobileVerificationContext,
          email,
        },
      };
    }

    case GLOBAL_CONTEXT_ACTION_TYPE.SET_IS_UPGRADE: {
      const isUpgrade = action.payload;

      return {
        ...globalContext,
        zenithAccountUpgradeContext: {
          ...globalContext.zenithAccountUpgradeContext,
          isUpgrade,
        },
      };
    }

    case GLOBAL_CONTEXT_ACTION_TYPE.SET_ACCOUNT_NUMBER: {
      const accountNumber = action.payload;

      return {
        ...globalContext,
        zenithAccountUpgradeContext: {
          ...globalContext.zenithAccountUpgradeContext,
          accountNumber,
        },
      };
    }

    case GLOBAL_CONTEXT_ACTION_TYPE.SET_PHONE_NUMBER: {
      const phoneNumber = action.payload;

      return {
        ...globalContext,
        mobileVerificationContext: {
          ...globalContext.mobileVerificationContext,
          phoneNumber,
        },
      };
    }

    case GLOBAL_CONTEXT_ACTION_TYPE.SET_FIRST_NAME: {
      const firstName = action.payload;
      return {
        ...globalContext,
        mobileVerificationContext: {
          ...globalContext.mobileVerificationContext,
          firstName,
        },
      };
    }

    case GLOBAL_CONTEXT_ACTION_TYPE.SET_IS_NEW_USER: {
      const isNewUser = action.payload;
      return {
        ...globalContext,
        mobileVerificationContext: {
          ...globalContext.mobileVerificationContext,
          isNewUser,
        },
      };
    }

    //#endregion Mobile Verification Context

    //#region Notification Context

    case GLOBAL_CONTEXT_ACTION_TYPE.SET_OPEN_NOTIFICATION: {
      const open = action.payload;

      return {
        ...globalContext,
        notificationContext: {
          ...globalContext.notificationContext,
          open,
        },
      };
    }

    case GLOBAL_CONTEXT_ACTION_TYPE.CLEAR_NOTIFICATION_CONTEXT: {
      return {
        ...globalContext,
        notificationContext: {
          ...initGlobalContext.notificationContext,
        },
      };
    }

    //#endregion Notification Context

    //#region Sign up Stepper Context

    case GLOBAL_CONTEXT_ACTION_TYPE.SET_ACTIVE_STEP: {
      const activeStep = action.payload;

      return {
        ...globalContext,
        appStepperContext: {
          ...globalContext.appStepperContext,
          activeStep,
        },
      };
    }

    case GLOBAL_CONTEXT_ACTION_TYPE.SET_MAX_STEP: {
      const maxStep = action.payload;

      return {
        ...globalContext,
        appStepperContext: {
          ...globalContext.appStepperContext,
          maxStep,
        },
      };
    }

    //#endregion Sign up Stepper Context

    //#region Payments Context

    case GLOBAL_CONTEXT_ACTION_TYPE.SET_PAYMENTS_CONTEXT: {
      const paymentsContext = action.payload;

      return {
        ...globalContext,
        paymentsContext,
      };
    }

    case GLOBAL_CONTEXT_ACTION_TYPE.CLEAR_PAYMENTS_CONTEXT: {
      return {
        ...globalContext,
        paymentsContext: {
          ...initGlobalContext.paymentsContext,
        },
      };
    }

    //#endregion Payments Context

    //#region Service failure Context
    case GLOBAL_CONTEXT_ACTION_TYPE.SET_SERVICE_FAILURE: {
      const hasError = action.payload;
      return {
        ...globalContext,
        serviceFailureContext: {
          ...globalContext.serviceFailureContext,
          hasError,
        },
      };
    }
    //#endregion Service failure Context

    //#region Send Money Context

    case GLOBAL_CONTEXT_ACTION_TYPE.SET_SEND_MONEY_CONTEXT: {
      const sendMoneyContext = action.payload;

      return {
        ...globalContext,
        sendMoneyContext,
      };
    }

    case GLOBAL_CONTEXT_ACTION_TYPE.CLEAR_SEND_MONEY_CONTEXT: {
      return {
        ...globalContext,
        sendMoneyContext: {
          ...initGlobalContext.sendMoneyContext,
        },
      };
    }

    //#endregion Send Money Context

    //#region Transactions Context

    case GLOBAL_CONTEXT_ACTION_TYPE.SET_TRANSACTIONS_CONTEXT: {
      const transactionsContext = action.payload;

      return {
        ...globalContext,
        transactionsContext,
      };
    }

    case GLOBAL_CONTEXT_ACTION_TYPE.CLEAR_TRANSACTIONS_CONTEXT: {
      return {
        ...globalContext,
        transactionsContext: {
          ...initGlobalContext.transactionsContext,
        },
      };
    }

    //#endregion Transactions Context

    //#region Selfies Context

    case GLOBAL_CONTEXT_ACTION_TYPE.SET_SELFIES_CONTEXT: {
      const selfiesContext = action.payload;

      return {
        ...globalContext,
        selfiesContext,
      };
    }

    //#endregion Selfies Context

    //#region Payment Link Context

    case GLOBAL_CONTEXT_ACTION_TYPE.SET_PAYMENT_LINK_CONTEXT: {
      const paymentLinkContext = action.payload;

      return {
        ...globalContext,
        paymentLinkContext,
      };
    }

    case GLOBAL_CONTEXT_ACTION_TYPE.CLEAR_PAYMENT_LINK_CONTEXT: {
      return {
        ...globalContext,
        paymentLinkContext: {
          ...initGlobalContext.paymentLinkContext,
        },
      };
    }

    //#region Loans Context

    case GLOBAL_CONTEXT_ACTION_TYPE.SET_LOANS_CONTEXT: {
      const loansContext = action.payload;

      return {
        ...globalContext,
        loansContext,
      };
    }

    case GLOBAL_CONTEXT_ACTION_TYPE.CLEAR_LOANS_CONTEXT: {
      return {
        ...globalContext,
        loansContext: {
          ...initGlobalContext.loansContext,
        },
      };
    }

    //#endregion Loans Context

    //#region POS Context

    case GLOBAL_CONTEXT_ACTION_TYPE.SET_POS_CONTEXT: {
      const posContext = action.payload;

      return {
        ...globalContext,
        posContext,
      };
    }

    case GLOBAL_CONTEXT_ACTION_TYPE.CLEAR_POS_CONTEXT: {
      return {
        ...globalContext,
        posContext: {
          ...initGlobalContext.posContext,
        },
      };
    }

    //#endregion POS Context

    //#region Registration Context

    case GLOBAL_CONTEXT_ACTION_TYPE.SET_REGISTRATION_CONTEXT: {
      const registrationContext = action.payload;

      return {
        ...globalContext,
        registrationContext,
      };
    }

    //#endregion Registration Context

    //#region SignUp Context

    case GLOBAL_CONTEXT_ACTION_TYPE.SET_SIGNUP_CONTEXT: {
      const signUpContext = action.payload;

      return {
        ...globalContext,
        signUpContext,
      };
    }

    //#endregion SignUp Context

    //#region User details context
    case GLOBAL_CONTEXT_ACTION_TYPE.SET_USER_DETAILS_CONTEXT: {
      const userDetailsContext = action.payload;

      return {
        ...globalContext,
        userDetailsContext: {
          ...globalContext.userDetailsContext,
          ...userDetailsContext,
        },
      };
    }
    //#endregion User details context

    //#region Settings Context

    case GLOBAL_CONTEXT_ACTION_TYPE.SET_SETTINGS_CONTEXT: {
      const settingsContext = action.payload;

      return {
        ...globalContext,
        settingsContext,
      };
    }

    case GLOBAL_CONTEXT_ACTION_TYPE.CLEAR_SETTINGS_CONTEXT: {
      return {
        ...globalContext,
        settingsContext: {
          ...initGlobalContext.settingsContext,
        },
      };
    }

    //#endregion Settings Context

    //#region Personal Details Context

    case GLOBAL_CONTEXT_ACTION_TYPE.SET_PERSONAL_DETAILS_CONTEXT: {
      const personalDetailsContext = action.payload;

      return {
        ...globalContext,
        personalDetailsContext,
      };
    }

    case GLOBAL_CONTEXT_ACTION_TYPE.CLEAR_PERSONAL_DETAILS_CONTEXT: {
      return {
        ...globalContext,
        personalDetailsContext: {
          ...initGlobalContext.personalDetailsContext,
        },
      };
    }

    //#endregion Personal Details Context

    //#region Upgrade Context

    case GLOBAL_CONTEXT_ACTION_TYPE.SET_UPGRADE_CONTEXT: {
      const upgradeContext = action.payload;

      return {
        ...globalContext,
        upgradeContext,
      };
    }

    case GLOBAL_CONTEXT_ACTION_TYPE.CLEAR_UPGRADE_CONTEXT: {
      return {
        ...globalContext,
        upgradeContext: {
          ...initGlobalContext.upgradeContext,
        },
      };
    }

    //#endregion Upgrade Context

    //#region Outlet Context

    case GLOBAL_CONTEXT_ACTION_TYPE.SET_OUTLET_CONTEXT: {
      const outletContext = action.payload;

      return {
        ...globalContext,
        outletContext: {
          ...globalContext.outletContext,
          ...outletContext,
        },
      };
    }

    case GLOBAL_CONTEXT_ACTION_TYPE.SET_OUTLETS: {
      const outlets: OutletListDTO[] = action.payload;

      return {
        ...globalContext,
        outletContext: {
          ...globalContext.outletContext,
          outlets,
          isLoadingOutlets: false,
          selection: reconcileOutletSelection(
            globalContext.outletContext.selection,
            outlets
          ),
        },
      };
    }

    case GLOBAL_CONTEXT_ACTION_TYPE.SET_OUTLET_SELECTION: {
      const selection: OutletContextType["selection"] = action.payload;

      return {
        ...globalContext,
        outletContext: {
          ...globalContext.outletContext,
          selection,
        },
      };
    }

    case GLOBAL_CONTEXT_ACTION_TYPE.CLEAR_OUTLET_CONTEXT: {
      return {
        ...globalContext,
        outletContext: {
          ...initGlobalContext.outletContext,
        },
      };
    }

    //#endregion Outlet Context

    default:
      throw new Error("Action not mapped");
  }
};
